
/**
 * PDF Utility for client-side parsing using pdf.js
 */

declare global {
  interface Window {
    pdfjsLib: any;
  }
}

export const readPdfTextWithY = async (file: File): Promise<Array<{text: string, y: number}>> => {
  if (!window.pdfjsLib) {
    throw new Error("PDF解析ライブラリが読み込まれていません。");
  }

  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = window.pdfjsLib.getDocument({ data: arrayBuffer });
  const pdf = await loadingTask.promise;
  const maxPages = pdf.numPages;
  let fullLines: Array<{text: string, y: number}> = [];

  for (let i = 1; i <= maxPages; i++) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();
    // ページに /Rotate（90度単位の回転）が設定されている場合、item.transform は
    // 回転前の座標系のままになる。viewportの変換を合成することで、実際の見た目通りの
    // 座標（x：左から右へ増加、y：上から下へ増加）に補正する。これを行わないと、
    // 回転済みPDFでは行と列の軸が入れ替わってしまい、行の分割・結合が崩れる。
    const viewport = page.getViewport({ scale: 1 });

    const items = textContent.items.map((item: any) => {
      const t = window.pdfjsLib.Util.transform(viewport.transform, item.transform);
      return {
        str: item.str,
        y: Math.round(t[5] * 10) / 10,
        x: t[4],
        width: item.width || 0,
        height: item.height || 10
      };
    });

    if (items.length === 0) continue;

    const rows: { [key: number]: any[] } = {};
    const rowTolerance = 2.0;

    items.forEach((item: any) => {
      let foundY = Object.keys(rows).find(y => Math.abs(Number(y) - item.y) < rowTolerance);
      if (foundY) {
        rows[Number(foundY)].push(item);
      } else {
        rows[item.y] = [item];
      }
    });

    // viewport変換後のyは上から下へ増加するため、昇順ソートで上から順に並べる。
    const sortedY = Object.keys(rows).map(Number).sort((a, b) => a - b);

    sortedY.forEach(y => {
      const lineItems = rows[y].sort((a, b) => a.x - b.x);

      // 大きなX方向ギャップ（列の境界）を検出し、複数エントリを分割
      const segments: any[][] = [[]];
      let lastXEnd = -1;
      lineItems.forEach(item => {
        if (lastXEnd !== -1) {
          const gap = item.x - lastXEnd;
          if (gap > 80) {
            segments.push([]);
          }
        }
        segments[segments.length - 1].push(item);
        lastXEnd = item.x + (item.width > 0 ? item.width : item.str.length * 6);
      });

      segments.forEach(segment => {
        let lineStr = "";
        let segLastXEnd = -1;
        segment.forEach(item => {
          if (segLastXEnd !== -1) {
            const gap = item.x - segLastXEnd;
            if (gap > 6) lineStr += "  ";
          }
          lineStr += item.str;
          segLastXEnd = item.x + (item.width > 0 ? item.width : item.str.length * 6);
        });
        if (lineStr.trim()) fullLines.push({ text: lineStr, y });
      });
    });
  }

  return fullLines;
};

export const readPdfText = async (file: File): Promise<string[]> => {
  const lines = await readPdfTextWithY(file);
  return lines.map(l => l.text);
};

let lastValidDate = ''; 

export const resetPdfParserState = () => {
    lastValidDate = '';
};

/**
 * 組合明細の1行をパースする
 */
export const parseUnionPdfLine = (line: string): any | null => {
  const trimmedLine = line.trim();
  if (!trimmedLine || trimmedLine.length < 5) return null;

  // ノイズ除去の強化
  // 空白を除去した文字列でもチェックを行う（例：「小 切 手」などを「小切手」として検知するため）
  const strippedLine = trimmedLine.replace(/\s+/g, '');
  
  // 除外対象キーワード（構造的ノイズ）
  // 明細行には決して含まれず、フッターやヘッダーにのみ現れる単語。行のどこに含まれていても除外する。
  const structuralNoise = [
      '前回', '繰越', '摘要', '受払', 'ページ', 'NO.', 'NO', '差引支給額'
  ];

  if (structuralNoise.some(n => trimmedLine.includes(n)) || structuralNoise.some(n => strippedLine.includes(n))) {
      return null;
  }

  // 集計欄ラベル（振込・手形・小切手・現金・各種「計」）
  // 業者名に含まれる場合（例：「（南生アスコンから振込）」）は明細行なので除外してはならない。
  // 集計欄は「ラベル＋金額」の形で日付を持たないため、日付を除いた本文の先頭がラベルで始まる場合のみ除外する。
  const summaryLabels = ['合計', '小計', '総計', '累計', '集計', '計', '振込', '手形', '小切手', '現金'];
  const dateInLine = trimmedLine.match(/(\d{1,2}月\d{1,2}日)/) || trimmedLine.match(/(\d{1,2}\/\d{1,2})/);
  const contentAfterDate = (dateInLine ? trimmedLine.replace(dateInLine[0], '') : trimmedLine).replace(/\s+/g, '');
  if (summaryLabels.some(label => contentAfterDate.startsWith(label))) {
      return null;
  }

  // 日付抽出
  const dateMatch = trimmedLine.match(/(\d{1,2}月\d{1,2}日)/) || trimmedLine.match(/(\d{1,2}\/\d{1,2})/);
  if (dateMatch) {
    lastValidDate = dateMatch[0];
  }

  if (!lastValidDate) return null;

  // 金額抽出ロジックの強化
  // 1. カンマ付きの数値 (例: 1,234)
  // 2. カンマなしだが3桁以上の数値 (例: 1000)
  // 3. マイナス記号付き
  const potentialNumbers = trimmedLine.match(/[-▲△－ー]?\d{1,3}(?:,\d{3})+(?:\.\d+)?|[-▲△－ー]?\d+/g);
  
  if (!potentialNumbers || potentialNumbers.length < 1) return null;

  // 明らかに日付の一部や小さなIDと思われる数値をフィルタリング
  const amountMatches = potentialNumbers.filter(s => {
      // 金額として妥当な条件（例: カンマがある、または数値が大きい、またはマイナス記号がある）
      if (s.includes(',') || s.match(/[-▲△－ー]/)) return true;
      // 単なる数字の場合、組合明細の文脈では桁数が多ければ金額とみなす
      return true;
  });

  // 日付文字列を除去した残りの文字列から数値を再抽出するほうが安全
  let contentPart = trimmedLine;
  if (dateMatch) contentPart = contentPart.replace(dateMatch[0], "");
  
  // 再抽出
  const refinedMatches = contentPart.match(/[-▲△－ー]?\d{1,3}(?:,\d{3})+(?:\.\d+)?|[-▲△－ー]?\d+/g);
  
  if (!refinedMatches || refinedMatches.length < 1) return null;

  // 数値を除去して名称を取得
  let namePart = contentPart;
  refinedMatches.forEach(m => { namePart = namePart.replace(m, ""); });
  
  // 名称のクリーニングと検証
  const clientName = namePart.split(/\s{2,}/).filter(c => c.trim().length > 0)[0]?.trim().replace(/^[\s・,、。]+/, "").replace(/[¥\\]+$/, "").trim() || "";

  // 1. 名称がない場合は無効
  if (!clientName) return null;
  
  // 2. 名称が短すぎる、または数字・記号のみの場合は無効
  if (clientName.length < 2 && !/[一-龠ぁ-んァ-ンa-zA-Z]/.test(clientName)) return null;
  if (/^[\d\s,.\-ー]+$/.test(clientName)) return null;

  const parseAmt = (s: string) => {
      const isNeg = /[-▲△－ー]/.test(s);
      const val = parseInt(s.replace(/[-▲△－ー ,]/g, '')) || 0;
      return isNeg ? -val : val;
  };

  let billingAmount = 0;
  let depositAmount = 0;

  if (refinedMatches.length >= 2) {
      billingAmount = parseAmt(refinedMatches[0]);
      depositAmount = parseAmt(refinedMatches[refinedMatches.length - 1]);
  } else if (refinedMatches.length === 1) {
      // 数値が1つしかない場合、通常は「入金」の列に数字があることが多い
      depositAmount = parseAmt(refinedMatches[0]);
  }

  // 金額が両方0の場合は無効データとして除外
  if (billingAmount === 0 && depositAmount === 0) return null;

  return {
      deposit_date: lastValidDate,
      client_abbreviation: clientName,
      category: "-", 
      shipping: "-", 
      deposit_amount: depositAmount,
      billing_amount: billingAmount,
      client_code: '', 
      client_name: '',
      difference: billingAmount - depositAmount
  };
};

/**
 * 南さつまリサイクル協同組合 受払計算書PDFの解析
 *
 * このPDFは「得意先名」列と「金額」列が異なるy座標範囲に格納されているため、
 * 通常のパーサーでは対応できない。以下の手順で正しくペアリングする：
 *   1. 金額行（日付＋金額）と名称行を分離して収集
 *   2. 名称行中の「得意先名」ヘッダーを境界として前後に分割
 *   3. 金額の日付グループ情報を用いてbeforeHeader内を並び替え
 *   4. 順序が揃った名称と金額を順番に対応付ける
 */
export const parseMinamisatsumaUnionPdf = (linesWithY: Array<{text: string, y: number}>): any[] => {
  // 日付・ヘッダー・合計欄など除外対象のパターン
  const NOISE = /合計|前回|繰越|受払|手形|小切手|現金|差引|ガイアテック|令和|R7S|㈱|殿|振込|過入金|売掛金額|摘要|日付|入金額|リサイクル|下記|お支払|締切|ページ/;
  const COMBINED_NOISE = /^振込$|^入金$|^支払$|^現金$|^合計$/;
  const DATE_PATTERN = /(\d{1,2}\/\d{1,2})/;
  const HAS_JAPANESE = /[一-龠ぁ-んァ-ン]/;
  const IS_HEADER = /得\s*意\s*先\s*名/;
  const HAS_AMOUNT = /\d{1,3}(?:,\d{3})+/;

  const parseAmt = (s: string): number => {
    const isNeg = /[-▲△－ー]/.test(s);
    const val = parseInt(s.replace(/[-▲△－ー ,]/g, '')) || 0;
    return isNeg ? -val : val;
  };

  const amountRows: Array<{y: number, date: string, amount: number}> = [];
  const nameItems: Array<{y: number, name: string, isHeader: boolean}> = [];
  // 名称＋金額が同じセグメントに含まれる場合の直接ペアリング用
  const combinedRows: Array<{y: number, date: string, amount: number, name: string}> = [];
  let lastDate = '';

  console.log('[MinamisatsumaParser] Total lines:', linesWithY.length);
  console.log('[MinamisatsumaParser] First 40 lines:', linesWithY.slice(0, 40).map(l => ({t: l.text.trim().substring(0, 40), y: Math.round(l.y)})));

  linesWithY.forEach(({text, y}) => {
    const t = text.trim();
    if (!t || t.length < 2) return;

    const stripped = t.replace(/\s+/g, '');
    if (NOISE.test(stripped)) return;

    // 日付検出：金額・日本語の有無に関係なく全行で更新
    const dateMatch = t.match(DATE_PATTERN);
    if (dateMatch) lastDate = dateMatch[1];

    if (IS_HEADER.test(t)) {
      nameItems.push({ y, name: t, isHeader: true });
      return;
    }

    const hasJp = HAS_JAPANESE.test(t);
    const hasAmt = HAS_AMOUNT.test(t);

    if (hasAmt && !hasJp) {
      // 数値のみの行（合計行などトークン過多は除外）
      const tokens = t.match(/\d{1,3}(?:,\d{3})+/g) || [];
      if (tokens.length > 5) return;
      const mainAmt = tokens.map(s => parseAmt(s)).find(n => n > 0) || 0;
      if (mainAmt > 0 && lastDate) {
        amountRows.push({ y, date: lastDate, amount: mainAmt });
      }
    } else if (hasJp && hasAmt) {
      // 名称と金額が同一セグメントに含まれる行（直接ペアリング）
      const tokens = t.match(/\d{1,3}(?:,\d{3})+/g) || [];
      if (tokens.length > 5) return;
      const mainAmt = tokens.map(s => parseAmt(s)).find(n => n > 0) || 0;
      // 数値を除去して名称を抽出
      let namePart = t;
      if (dateMatch) namePart = namePart.replace(dateMatch[0], '');
      tokens.forEach(tk => { namePart = namePart.replace(tk, ''); });
      const name = namePart.replace(/[¥\\.,\s　（）()]+/g, ' ').trim();
      if (mainAmt > 0 && lastDate && name.length >= 2 && HAS_JAPANESE.test(name)) {
        combinedRows.push({ y, date: lastDate, amount: mainAmt, name });
      }
    } else if (hasJp && !hasAmt) {
      // 名称のみの行
      if (stripped.length > 20) return;
      nameItems.push({ y, name: t, isHeader: false });
    }
  });

  console.log('[MinamisatsumaParser] amountRows:', amountRows.length, amountRows);
  console.log('[MinamisatsumaParser] combinedRows:', combinedRows.length, combinedRows);
  console.log('[MinamisatsumaParser] nameItems:', nameItems.length, nameItems);

  // 日付グループを収集（金額行は正しい視覚順序）
  const dateOrder: string[] = [];
  const dateCount: Map<string, number> = new Map();
  amountRows.forEach(r => {
    if (!dateCount.has(r.date)) {
      dateOrder.push(r.date);
      dateCount.set(r.date, 0);
    }
    dateCount.set(r.date, (dateCount.get(r.date) || 0) + 1);
  });

  // 名称行の並び替え
  // 抽出順（y降順）：[最終日付グループ][中間日付グループ群][ヘッダー][最初の日付グループ]
  // 正しい視覚順：[最初][中間][最終]
  const headerIdx = nameItems.findIndex(l => l.isHeader);
  const nonHeaderNames = nameItems.filter(l => !l.isHeader);

  let orderedNames: string[];

  if (headerIdx === -1 || dateOrder.length < 2) {
    orderedNames = nonHeaderNames.map(l => l.name);
  } else {
    const beforeHeader = nameItems.slice(0, headerIdx).filter(l => !l.isHeader);
    const afterHeader = nameItems.slice(headerIdx + 1).filter(l => !l.isHeader);

    const nLast = dateCount.get(dateOrder[dateOrder.length - 1]) || 0;

    if (nLast >= beforeHeader.length) {
      // グループ分割不要
      orderedNames = [
        ...afterHeader.map(l => l.name),
        ...beforeHeader.map(l => l.name)
      ];
    } else {
      // beforeHeader の先頭 nLast 件が最終日付グループ、残りが中間グループ
      orderedNames = [
        ...afterHeader.map(l => l.name),
        ...beforeHeader.slice(nLast).map(l => l.name),
        ...beforeHeader.slice(0, nLast).map(l => l.name)
      ];
    }
  }

  // 金額と名称を順番に対応付け（分離セグメント方式）
  // ※後段でy座標による視覚順ソートを行うため、各エントリに一時フィールド _y を持たせる。
  //   分離方式の行は amountRows[i].y を、結合方式の行は combinedRows[k].y を用いる。
  const count = Math.min(amountRows.length, orderedNames.length);
  const tmpResults: any[] = [];
  for (let i = 0; i < count; i++) {
    tmpResults.push({
      _y: amountRows[i].y,
      deposit_date: amountRows[i].date,
      client_abbreviation: orderedNames[i],
      deposit_amount: amountRows[i].amount,
      billing_amount: 0,
      client_code: '',
      client_name: '',
      category: '-',
      shipping: '-',
      difference: -amountRows[i].amount
    });
  }

  // combinedRows（名称＋金額が同一セグメント）をノイズ除去して追加
  const validCombined = combinedRows.filter(r =>
    r.name.length >= 3 && !COMBINED_NOISE.test(r.name)
  );
  console.log('[MinamisatsumaParser] validCombined after filter:', validCombined.length, validCombined);
  validCombined.forEach(r => {
    tmpResults.push({
      _y: r.y,
      deposit_date: r.date,
      client_abbreviation: r.name,
      deposit_amount: r.amount,
      billing_amount: 0,
      client_code: '',
      client_name: '',
      category: '-',
      shipping: '-',
      difference: -r.amount
    });
  });

  // 視覚順（PDF上での上から下）にソートする。
  // readPdfTextWithYはページ回転を考慮したviewport変換後の座標を返すため、
  // yは上端を0として下方向へ増加する。よって昇順ソートで上から並ぶ。
  // これをしないと「分離方式の行→結合方式の行」の出現順固定となり、
  // 1枚のPDF内に両方が混在した場合に並び順が崩れる（西垂水・堀之内が末尾に飛ぶ等）。
  tmpResults.sort((a, b) => a._y - b._y);

  // ソート用の一時フィールドを除去して返却
  const results = tmpResults.map(({ _y, ...rest }) => rest);

  console.log('[MinamisatsumaParser] Final results count:', results.length);
  return results;
};

/**
 * でんさい発生記録PDFの解析
 */
export const parseDensaiPdf = (lines: string[]): any[] => {
  // 単一のレコードまたはリスト形式を想定
  // キーワード: "利用者番号", "債権金額", "支払期日", "記録番号"
  
  const records: any[] = [];
  
  // State machine variables
  let currentUserCode = '';
  let currentRemitterName = '';
  let currentAmount = 0;
  let currentDate = '';
  let currentRecordNum = '';
  
  // 行ベースで探索
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // 利用者番号の行を探す (債務者情報セクション)
    if (line.includes("利用者番号") || line.match(/[0-9A-Z]{9}/)) {
       const match = line.match(/([0-9A-Z]{9})/);
       if (match) {
           if (!currentUserCode) currentUserCode = match[1];
           else if (currentUserCode === '00008CPV6' && match[1] !== '00008CPV6') {
               currentUserCode = match[1];
           }
       }
    }
    
    // 法人名
    if ((line.includes("法人名") || line.includes("個人事業者名")) && !currentRemitterName) {
        // 同じ行にあるか、次の行にあるか
        const parts = line.split(/名\s+/);
        if (parts.length > 1 && parts[1].trim()) {
            currentRemitterName = parts[1].trim();
        } else if (i + 1 < lines.length) {
            // 次の行を見る
            const nextLine = lines[i+1].trim();
            if (!nextLine.includes("部署名") && !nextLine.includes("住所")) {
                currentRemitterName = nextLine;
            }
        }
    }

    // 債権金額
    if (line.includes("債権金額") && currentAmount === 0) {
        const match = line.match(/([\d,]+)\s*円?/);
        if (match) {
            currentAmount = parseInt(match[1].replace(/,/g, ''));
        }
    }

    // 支払期日
    if (line.includes("支払期日") && !currentDate) {
        const match = line.match(/(\d{4}年\d{1,2}月\d{1,2}日)/);
        if (match) {
            currentDate = match[1].replace(/年/g, '/').replace(/月/g, '/').replace(/日/g, '');
        }
    }
    
    // 記録番号
    if (line.includes("記録番号") && !currentRecordNum) {
        const match = line.match(/([0-9A-Z]{20})/);
        if (match) {
            currentRecordNum = match[1];
        }
    }
  }
  
  // 抽出できた場合、レコードとして返す
  if (currentAmount > 0 && currentDate) {
      records.push({
          densai_user_code: currentUserCode, // 取得した利用者番号
          remitter_name: currentRemitterName || '不明',
          amount: currentAmount,
          payment_date: currentDate,
          record_number: currentRecordNum || `UNKNOWN-${Date.now()}` // キーとして必要
      });
  }

  return records;
};
